import { useState, useCallback, useEffect } from 'react';
import { Player } from '../../app/player/Player';
import { useBeaconContext } from './BeaconConnectionWrapper';
import { QuickstartCard } from '../quickstart/components/QuickstartCard';
import { ANIMATION_PRESETS } from '../quickstart/config/animations';
import { BEACON_THEME } from './beaconTheme';
import { Button } from '@playcastdotio/Shadcn';
import { ReconnectingOverlay } from '../../app/player/quickstart/ReconnectingOverlay';
import { SignalQualityIndicator } from '../quickstart/components/SignalQualityIndicator';
import { playerConnectionState } from '../../api/stores/playerStores';
import { MobileGamepadInputStandalone, MobileGamepadUiStandalone, type MobileGamepadInputEvent } from '@playcastdotio/MobileGamepad';
import { setGamepadState } from '../../api/modules/hostMessaging/inputEvents';
import { BrowserLockManager } from '../_base/BrowserLockManager';
import { signalBarsState } from '../../api/stores/signalBarsStore';

const lockManager = new BrowserLockManager();

const requestBrowserLocks = async () => {
  try {
    const videoElement = document.querySelector('video');
    if (videoElement) {
      await videoElement.play().catch(() => {});
    }

    await lockManager.requestLocks();
  } catch {
    // Locks are best-effort; continue without them
  }
};

interface BeaconPlayerWrapperProps {
  debugMobileGamepad?: boolean;
}

export const BeaconPlayerWrapper = ({ debugMobileGamepad = false }: BeaconPlayerWrapperProps) => {
  const { state, coordinator } = useBeaconContext();
  const [showPlayOverlay, setShowPlayOverlay] = useState(true);
  const [isRequesting, setIsRequesting] = useState(false);
  const [signalQuality, setSignalQuality] = useState(playerConnectionState.currentState.signalQuality);
  const [signalBarsEnabled, setSignalBarsEnabled] = useState(signalBarsState.currentState.enabled);
  // Single latched reconnect cover (5A) — coordinator + stall, held across media clears.
  const [reconnectCoverLatched, setReconnectCoverLatched] = useState(false);

  const handleSignalBarsToggle = useCallback(() => {
    setSignalBarsEnabled(!signalBarsState.currentState.enabled);
  }, []);

  const handlePlayNow = useCallback(async () => {
    if (isRequesting) return;
    setIsRequesting(true);
    await requestBrowserLocks();
    setShowPlayOverlay(false);
  }, [isRequesting]);

  const handleDisconnect = () => {
    coordinator?.disconnectPeer('beacon:user_disconnect');
  };

  const handleMobileGamepadInput = useCallback((event: MobileGamepadInputEvent) => {
    if (event.gamepadState) {
      setGamepadState(event.gamepadState);
    }
  }, []);

  useEffect(() => {
    const id = playerConnectionState.addListener((connState) => {
      setSignalQuality(connState.signalQuality);
    });
    return () => playerConnectionState.removeListener(id);
  }, []);

  useEffect(() => {
    const onFullscreenExit = () => {
      if (!document.fullscreenElement) {
        setShowPlayOverlay(true);
        setIsRequesting(false);
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenExit);
    const preventDrag = (e: DragEvent) => e.preventDefault();
    const preventContextMenu = (e: MouseEvent) => e.preventDefault();
    document.addEventListener('dragstart', preventDrag);
    document.addEventListener('contextmenu', preventContextMenu);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenExit);
      document.removeEventListener('dragstart', preventDrag);
      document.removeEventListener('contextmenu', preventContextMenu);
    };
  }, []);

  const showGamepad = debugMobileGamepad && !showPlayOverlay;
  const terminalPhase =
    state.phase === 'disconnected' ||
    state.phase === 'error' ||
    state.phase === 'kicked' ||
    state.phase === 'not_found';

  return (
    <div
      className='relative h-screen w-screen select-none overflow-hidden bg-black'
      style={{ touchAction: 'none', WebkitTouchCallout: 'none' } as React.CSSProperties}
    >
      <Player
        reconnectingSignal={state.isReconnecting}
        onReconnectCoverLatchedChange={setReconnectCoverLatched}
        reconnectCoverAbandoned={terminalPhase || (showPlayOverlay && !state.isReconnecting)}
      />

      <SignalQualityIndicator level={signalQuality} connectionDown={reconnectCoverLatched} enabled={signalBarsEnabled} />

      {showPlayOverlay && (
        <div className='absolute inset-0 z-[100] flex items-center justify-center'>
          <div className='pointer-events-auto'>
            <QuickstartCard
              theme={BEACON_THEME}
              animation={ANIMATION_PRESETS.cardDefault}
              showHeader={false}
              hideFooter
            >
              <div className='flex flex-col items-center gap-10 px-8 py-12 text-center'>
                {state.streamTitle && (
                  <h2 className='text-2xl font-semibold text-white'>{state.streamTitle}</h2>
                )}
                <img
                  src='/quickstart/pbp.png'
                  alt='Powered by Playcast'
                  className='h-12'
                />
                <Button
                  onClick={handlePlayNow}
                  disabled={isRequesting}
                  className='rounded-full border-2 border-black px-8 py-4 text-lg font-bold shadow-lg hover:opacity-90'
                  style={{ backgroundColor: '#FACC15', color: '#000' }}
                  size='lg'
                >
                  {isRequesting ? 'Starting...' : 'Click Here To Join'}
                </Button>
              </div>
            </QuickstartCard>
          </div>
        </div>
      )}

      {!showPlayOverlay && (
        <div className='absolute left-4 top-4 z-20 opacity-0 transition-opacity duration-200 hover:opacity-100'>
          <button
            onClick={handleDisconnect}
            className='rounded-lg bg-black/60 px-3 py-1.5 text-xs font-medium text-zinc-400 backdrop-blur-sm transition-colors hover:bg-black/80 hover:text-white'
          >
            Disconnect
          </button>
        </div>
      )}

      {showGamepad && (
        <>
          <MobileGamepadInputStandalone
            enabled={true}
            access={{ mouse: true, keyboard: true, touch: true, gamepad: true }}
            onInput={handleMobileGamepadInput}
          />
          <div className='fixed inset-0 z-[1000]'>
            <MobileGamepadUiStandalone
              enabled={true}
              testMode={true}
              gamepadSlot={-1}
              isMouseEnabled={true}
              isKeyboardEnabled={true}
              disconnect={handleDisconnect}
              isPhysicalGamepadConnected={false}
            signalBarsToggle={handleSignalBarsToggle}
            signalBarsOn={signalBarsEnabled}
            />
          </div>
        </>
      )}

      {/* Single latched reconnect cover (5A) — attempt counter intentionally hidden */}
      <ReconnectingOverlay visible={reconnectCoverLatched} />
    </div>
  );
};
